<template>
  <div class="view-wrapper">
    <ViewHeader title="Plans"></ViewHeader>
    <ViewMain>
      <DataTable
        :value="currentPlans"
        class="p-mb-4"
      >
        <Column columnKey="name" header="Name" sortable>
          <template #body="slotProps">
            <div
              class="p-d-flex p-jc-between"
              :class="!!slotProps.data.parentId.intID ? 'p-ml-5' : ''"
            >
              <div class="p-d-flex p-ai-center">{{ slotProps.data.name }}</div>
              <div class="p-d-flex">
                <Button
                  v-if="canExportData"
                  label="Export Data"
                  icon="pi pi-download"
                  class="p-mr-2"
                  @click="exportPlan(slotProps.data.id)"
                ></Button>
                <Button
                  label="Launch Plan"
                  @click="launchPlan(slotProps.data.id)"
                  :disabled="!canLaunchPlan"
                ></Button>
              </div>
            </div>
          </template>
        </Column>
        <template #loading>
          <h3>Loading plans...</h3>
        </template>
        <template #empty>
          <h3>No plans available.</h3>
        </template>
      </DataTable>
      <!-- Also shown when the account is inactive but has no grace period end date, so users still get the message and export option -->
      <div v-if="canExportData" style="margin-block-start: 4rem; margin-inline: auto; max-width: 60rem;">
        <p v-if="isCurrentUserSuperUser" class="super-user-account-notice">Super User view: users on this account can't open these plans.</p>
        <template v-if="this.$store.getters.currentAccount.showChargebeeUI">
          <h2>Your Annum subscription has ended</h2>
          <p>Your plans are no longer available to view or edit.</p>
          <p>Account Owner: To continue using Annum, select Subscriptions from the menu under the black dot in the lower-left corner to activate your Starter subscription, or contact us at <a href="mailto:support@AnnumPlanning.com">support@AnnumPlanning.com</a> for more options.</p>
        </template>
        <template v-else>
          <h2>Your Annum subscription has ended</h2>
          <p>Your plans are no longer available to view or edit.</p>
          <p>Account Owner: To continue using Annum, contact us at <a href="mailto:support@AnnumPlanning.com">support@AnnumPlanning.com</a> to activate your subscription.</p>
        </template>
        <template v-if="isAccountInGracePeriod">
          <h3 class="p-mt-6">Activate your subscription or export your data by {{ gracePeriodEndsAtLabel }}</h3>
          <p>After this date, your account and plan data will be permanently deleted.</p>
        </template>
        <h3 v-else class="p-mt-6">Activate your subscription or export your data</h3>
      </div>
      <!-- Super users can still open plans on an expired account, so show the expired message inline instead of the blocking overlay -->
      <div
        v-if="isAccountGracePeriodOver"
        :class="isCurrentUserSuperUser ? 'inactive-account-message' : 'inactive-account-overlay'"
      >
        <p v-if="isCurrentUserSuperUser" class="super-user-account-notice">Super User view: users on this account can't open these plans.</p>
        <h2>Your Annum subscription has ended</h2>
        <p>Your plans are no longer available to view or edit.</p>
        <p>You can contact us at <a href="mailto:support@AnnumPlanning.com">support@AnnumPlanning.com</a>.</p>
      </div>
    </ViewMain>
  </div>
</template>

<script lang="ts">
import Vue from 'vue'
import ViewHeader from '@/components/ViewHeader.vue'
import ViewMain from '@/components/ViewMain.vue'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import ID from '@/models/ID'
import { AlertMessageSeverity } from '@/models/AlertMessage'
import type { PlanExportResponse } from '@/services/PlansService'

import Dialog from 'primevue/dialog'

Vue.component('Column', Column)
Vue.component('DataTable', DataTable)
Vue.component('Dialog', Dialog)

export default Vue.extend({
  name: 'PlanList',
  components: {
    ViewHeader,
    ViewMain,
  },
  computed: {
    currentPlans() {
      if (this.$store.getters.isCurrentUserSuperAdmin) {
        return this.$store.getters.allPlans
      }
      return this.$store.getters.allPlans.filter((plan) => {
        if (
          plan.userRoles.filter(
            (userRole) =>
              userRole.user.id.intID == this.$store.getters.currentUser.id.intID
          ).length
        ) {
          return true
        }
        return false
      })
    },
    isAccountActive(): boolean {
      return this.$store.getters.currentAccount.isActive
    },
    isCurrentUserSuperUser(): boolean {
      return this.$store.getters.isCurrentUserSuperAdmin
    },
    canLaunchPlan(): boolean {
      return this.isAccountActive || this.isCurrentUserSuperUser
    },
    isAccountInGracePeriod(): boolean {
      return this.$store.getters.currentAccount.isInGracePeriod
    },
    isAccountGracePeriodOver(): boolean {
      return this.$store.getters.currentAccount.isGracePeriodOver
    },
    isAccountInactiveWithoutGracePeriodDate(): boolean {
      return this.$store.getters.currentAccount.isInactiveWithoutGracePeriodDate
    },
    // Export is available during the grace period, and on inactive accounts the API has no grace period end date for
    canExportData(): boolean {
      return this.isAccountInGracePeriod || this.isAccountInactiveWithoutGracePeriodDate
    },
    gracePeriodEndsAtLabel(): string {
      return this.$store.getters.currentAccount.gracePeriodEndsAt?.toLocaleDateString('en-US', {year: 'numeric', month: 'long', day: 'numeric'}) ?? ''
    },
  },
  methods: {
    launchPlan(planId: ID) {
      this.$store.commit('updateIsUIBlocked', true)

      this.$store.dispatch('updateCurrentPlanById', planId).then(() => {
        this.$store.commit('updateIsUIBlocked', false)
        this.$router.push(
          `/account/${this.$store.getters.currentAccount.id.intID}/plan/${planId.intID}/calendar/month`
        )
      })
    },
    exportPlan(planId: ID) {
      // No dates: the API exports each plan from its createdAt through today
      this.$store.getters.services.plans.exportPlans([planId.intID], '', '', ['tactics', 'initiatives']).then(
        (response: PlanExportResponse) => {
          window.open(response.url, '_blank')
        },
        (error) => {
          Vue.prototype.$toast.add({
            severity: AlertMessageSeverity.error,
            summary: 'Error exporting plan data.',
            detail: error,
          })
        }
      )
    },
  },
})
</script>

<style lang="scss">
.plan-settings-tutorial {
  .plan-settings-modal-header-tutorial {
    position: sticky;
    top: 0;
    z-index: 1;
    text-align: center;
    align-items: center;
    padding: 5.6rem 2.4rem 1rem 1.6rem;
    background-color: #f9f9f9;

    h2 {
      margin-bottom: 0;
    }
    .p-button-outlined {
      z-index: 1; //Fixing issue with other buttons overlapping outlined buttons on initial render
    }
  }
  .plan-settings-modal-content {
    padding: 1.6rem;
  }
  .p-dialog {
    // overflow-y: scroll;
    overflow-y: visible;
    max-height: 100vh;
    width: 80%;
  }
  & > .p-dialog-content {
    padding: 0;
  }
}
.inactive-account-overlay {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background-color: rgba(255, 255, 255, 0.5);
  display: flex;
  justify-content: center;

  h2 {
    position: absolute;
    top: calc(50vh - 7.2rem);
  }
}
</style>
